<script>
import {useRoute, useRouter} from "vue-router";
import {RecurringDonation} from "@/javascript/stores/donationSession";
import {useDonationStore} from "@/javascript/stores/donation";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import {OneTimeDonation} from "../../../stores/donationSession";
import {useI18n} from "vue-i18n";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import Popup from "@/javascript/views/components/popup.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";

export default {
  components: {
    LoadingSpinner,
    Popup
  },
  setup() {
    // Router
    const route = useRoute();
    const router = useRouter();

    // i18n
    const {t} = useI18n();

    const donationStore = useDonationStore();
    const analyticEventStore = useAnalyticEventStore();


    return {
      route,
      router,
      t,
      donationStore,
      analyticEventStore,
    };
  },
  data() {
    return {
      editing: false,
      saving: false,
      popupMessage: "",
      sendingEmail: false
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    donation() {
      return this.donationStore.currentDonation;
    }
  },
  mounted() {
    this.donationStore.getDonation(this.route.params.id);
    this.analyticEventStore.logEvent({ action: 'donation-view-detail' });
  },
  methods: {
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();

    },
    resendEmailReceipt() {
      if(this.sendingEmail) return;
      this.sendingEmail = true;

      this.donationStore.resendDonationReceipt(this.donation)
        .then(() => {
          this.sendingEmail = false;
          this.showPopup("Email Sent!");
        })
        .catch((error) => {
          this.sendingEmail = false;
          this.showPopup("Something went wrong!");
        });
    },
    RecurringDonation() {
      return RecurringDonation;
    },
    back() {
      this.router.back();
    },
    navigateToDonorView(memberId) {
      this.analyticEventStore.logEvent({
        action: `donation-view-navigate-to-donor`,
        traceable_type: 'Member',
        traceable_id: memberId
      });
      this.router.push({name: 'client-dashboard', params: {id: memberId, org: this.route.params.org, panel: 'donors'}});
    },
    toggleEdit() {
      this.analyticEventStore.logEvent({
        action: `donation-view-edit-donation`,
        traceable_type: 'Donation',
        traceable_id: this.donation.id
      });
      this.donationStore.toggleEdit();
    },
    async save() {
      this.analyticEventStore.logEvent({
        action: `donation-view-save-donation`,
        traceable_type: 'Donation',
        traceable_id: this.donation.id
      });
      await this.donationStore.saveDonation();
    }
  }
};
</script>

<template>
  <div class="w-full p-4 show-table">
    <!-- Error state -->
    <div
      v-if="donationStore.donationError"
      class="bg-white rounded-xl shadow-lg p-6 text-center text-gray-500"
    >
      <i class="fa-solid fa-circle-exclamation text-2xl text-gray-300 mb-2" />
      <p>Unable to load this donation. It may have been removed or you may not have access.</p>
      <button
        class="btn-outline mt-4"
        @click="router.back()"
      >
        Go Back
      </button>
    </div>
    <div
      v-else
      class="bg-white rounded-xl shadow-lg p-6 pb-50"
    >
      <!-- Table Header -->
      <div class="mb-4 border-b pb-2 flex justify-between items-center">
        <h2 class="font-bold">
          Gift Details
        </h2>
        <div
          role="button"
          class="btn-outline min-w-32 flex items-center justify-center"
          :class="{disabled: sendingEmail}"
          @click="resendEmailReceipt();"
        >
          <div v-if="sendingEmail">
            <loading-spinner size="icon-small" />
          </div>
          <div v-else>
            Resend Receipt
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <!-- Table Body -->
        <table class="table-body min-w-full border-collapse bg-white">
          <tbody>
            <template v-if="donationStore.loadingDonation">
              <tr
                v-for="i in 8"
                :key="i"
                class="animate-pulse"
              >
                <td class="w-1/2 p-2">
                  <div class="h-4" />
                </td>
                <td class="w-1/2 p-2">
                  <div class="h-4" />
                </td>
              </tr>
            </template>
            <template v-else>
              <tr
                :class="!donationStore.givingBlind ? 'cursor-pointer user-info-row' : ''"
                @click="!donationStore.givingBlind && navigateToDonorView(donation.donor_member.id)"
              >
                <td>User</td>
                <td>{{ [donation.user?.first_name, donation.user?.last_name].filter(Boolean).join(' ') }}</td>
              </tr>
              <tr>
                <td>Organization</td>
                <td>{{ donation.organization_name }}</td>
              </tr>

              <tr>
                <td>Donation Target</td>
                <td>
                  <div v-if="donationStore.editing">
                    <select
                      id="fund"
                      v-model="donationStore.localDonation.donatable"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                      <option
                        v-for="target in donationStore.donationTargets"
                        :key="target.id"
                        :value="target"
                      >
                        {{ target.model + ' - ' + target.name }}
                      </option>
                    </select>
                  </div>
                  <div v-else>
                    {{ donation.donatable ? donation.donatable.name : "No Target Set" }}
                  </div>
                </td>
              </tr>
              <tr v-if="donation.type === RecurringDonation()">
                <td>Frequency</td>
                <td> {{ donationStore.intervalString(donation) }}</td>
              </tr>
              <tr>
                <td>Amount</td>
                <td>{{ ApplicationHelper.formatMoney(donation.amount) }}</td>
              </tr>
              <tr>
                <td>Payment Method</td>
                <td>{{ ApplicationHelper.formatPaymentProcessorType(donation.processor_type) }}</td>
              </tr>
              <tr>
                <td>Gift Date</td>
                <td>{{ ApplicationHelper.formatDate(donation.paid_at, {weekday: false, time: false}) }}</td>
              </tr>
              <tr v-if="donation.type === RecurringDonation()">
                <td v-if="donation.status === 'trialing'">
                  Donation Start Date
                </td>
                <td v-else>
                  Next Payment Date
                </td>
                <td>{{ ApplicationHelper.formatDate(donation.next_payment_date, {weekday: false, time: false}) }}</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>{{ t('donation.status.' + [donation.status]) }}</td>
              </tr>
              <tr v-if="donation.type === RecurringDonation()">
                <td>Successful Payments</td>
                <td>{{ donation.successful_payments_count }}</td>
              </tr>
              <tr>
                <td>Notes</td>
                <td>
                  <div v-if="donationStore.editing">
                    <input
                      id="notes"
                      v-model="donationStore.localDonation.notes"
                      type="text"
                      class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                  </div>
                  <div v-else>
                    {{ donation.notes }}
                  </div>
                </td>
              </tr>
            </template>
            <tr>
              <th class="pt-4">
                <div class="flex space-x-2">
                  <button
                    class="btn-outline"
                    @click="back"
                  >
                    Back
                  </button>
                  <button
                    :class="donationStore.editing ? 'btn-outline' : 'btn-filled'"
                    @click="toggleEdit"
                  >
                    {{ donationStore.editing ? 'Cancel' : 'Edit' }}
                  </button>
                  <button
                    v-if="donationStore.editing"
                    class="btn-filled"
                    @click="save"
                  >
                    Save
                  </button>
                </div>
              </th>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <popup
      ref="popup"
      :message="popupMessage"
      :duration="1400"
    />
  </div>

  <!--  <div class="w-full p-4">-->
  <!--    <div class="bg-white rounded-xl shadow-lg p-6 p-b-50">-->
  <!--      <h2 class="p-b-20 font-bold">-->
  <!--        User Info-->
  <!--      </h2>-->
  <!--      <div class="overflow-x-auto">-->
  <!--        <table class="min-w-full border-collapse bg-white">-->
  <!--          <thead class="border-b">-->
  <!--            <tr>-->
  <!--              <th>User</th>-->
  <!--              <th>Organization</th>-->
  <!--            </tr>-->
  <!--          </thead>-->
  <!--          <tbody>-->
  <!--            <template v-if="donationStore.loadingDonation">-->
  <!--              <tr class="animate-pulse">-->
  <!--                <td class="w-1/4 p-2">-->
  <!--                  <div class="h-6 bg-gray-300 rounded" />-->
  <!--                </td>-->
  <!--              </tr>-->
  <!--            </template>-->
  <!--            <template v-else>-->
  <!--              <tr-->
  <!--                class="cursor-pointer user-info-row"-->
  <!--                @click="navigateToDonorView(donation.donor_member.id)"-->
  <!--              >-->
  <!--                <td class="w-1/2">-->
  <!--                  {{ donation.user?.email }}-->
  <!--                </td>-->
  <!--                <td class="w-1/2">-->
  <!--                  {{ donation.organization_name }}-->
  <!--                </td>-->
  <!--              </tr>-->
  <!--            </template>-->
  <!--          </tbody>-->
  <!--        </table>-->
  <!--      </div>-->
  <!--    </div>-->
  <!--  </div>-->
</template>

<style scoped lang="scss">
.table-body {
  tr:nth-child(odd) {
    @apply bg-table_gray;
  }

  tr:nth-child(even) {
    @apply bg-white;
  }

  tr:last-child {
    @apply bg-white;
  }
}

th {
  width: 25%;
  font-size: 10px;
  font-weight: normal;
  text-align: left;
  text-transform: uppercase;
  @apply text-slate-500;
  @apply px-2;
  @apply py-2;
}

td {
  width: 25%;
  font-size: 12px;
  @apply px-2;
  @apply py-4;
}

.user-info-row:hover {
  color: darkcyan;
  background-color: #f1f5f9; /* light gray background on hover */
}

.export-button {
  @apply text-slate-500;
  @apply border border-slate-500;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, border 0.3s;
}

.export-button:hover {
  @apply text-white;
  background-color: #081225;
}

@media screen and (max-width: 720px) {
  .flex {
    flex-direction: row;
    align-items: center;
  }
}
</style>
